/**
 * LangAlternatesManager — global injector of <link rel="alternate" hreflang>
 * tags for every navigation, so Google can pair every EN page with its ES
 * counterpart (and vice versa) without relying on per-page Helmet hydration.
 *
 * Why: GSC was flagging 123 pages as "Alternate page with proper canonical
 * tag" because the static index.html ships zero hreflang, and Helmet only
 * adds them after React mounts. Googlebot's render snapshot frequently
 * captures the pre-hydration state, so the pair never gets registered.
 *
 * This component lives next to <RouteRobotsManager> at the root of the
 * router. On every pathname change it:
 *   1. Strips any existing hreflang links that DON'T carry data-fwd-locked
 *      (page-level imperative locks set by <ServiceAreaPage> stay intact).
 *   2. Resolves the EN ↔ ES counterpart via the routeMap helper.
 *   3. Appends en, en-US, x-default, es, es-US <link rel="alternate"> tags.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { toEs, toEn } from '@/i18n/routeMap';

const BASE = 'https://fortworthdental.com';

const setHreflangs = (currentPath) => {
  if (typeof document === 'undefined') return;

  // Don't touch locked tags — those are page-level imperative locks set by
  // ServiceAreaPage / SpanishBlogPost / etc.
  document.head
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((el) => {
      if (!el.dataset.fwdLocked) el.remove();
    });

  // If a locked one already exists, the page knows what it's doing — bail.
  if (document.head.querySelector('link[rel="alternate"][hreflang][data-fwd-locked="1"]')) {
    return;
  }

  // Resolve the language pair.
  const isEs = currentPath === '/es' || currentPath.startsWith('/es/');
  let enPath;
  let esPath;
  try {
    if (isEs) {
      esPath = currentPath;
      enPath = toEn(currentPath);
    } else {
      enPath = currentPath;
      esPath = toEs(currentPath);
    }
  } catch {
    return;
  }
  if (!enPath || !esPath) return;

  const enUrl = `${BASE}${enPath}`;
  const esUrl = `${BASE}${esPath}`;

  const alts = [
    { hreflang: 'en', href: enUrl },
    { hreflang: 'en-US', href: enUrl },
    { hreflang: 'x-default', href: enUrl },
    { hreflang: 'es', href: esUrl },
    { hreflang: 'es-US', href: esUrl },
  ];

  alts.forEach(({ hreflang, href }) => {
    const link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', hreflang);
    link.setAttribute('href', href);
    link.dataset.fwdRouteAlt = '1';
    document.head.appendChild(link);
  });
};

const LangAlternatesManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    setHreflangs(pathname);
    // Re-assert after Helmet's async reconciliation passes. Without these
    // re-asserts, on EN static pages Helmet can wipe imperatively-added
    // link alternates during its initial reconciliation pass.
    const t1 = setTimeout(() => setHreflangs(pathname), 50);
    const t2 = setTimeout(() => setHreflangs(pathname), 500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [pathname]);
  return null;
};

export default LangAlternatesManager;
